<template>
  <el-dialog
    :visible.sync="dialogVisible"
    :title="dialogTitle"
    width="980px"
    :close-on-click-modal="false"
    @closed="resetForm"
  >
    <el-form
      ref="formRef"
      :model="formData"
      :rules="rules"
      label-width="150px"
      size="mini"
      class="dialog-form"
    >
      <el-form-item label="店铺" prop="storeId">
        <el-select v-model="formData.storeId" placeholder="请选择店铺" style="width: 320px">
          <el-option
            v-for="item in storeOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </el-form-item>

      <el-form-item label="模板名称" prop="templateName">
        <el-input
          v-model.trim="formData.templateName"
          maxlength="30"
          placeholder="请输入模板名称"
          style="width: 320px"
          :disabled="isEditMode"
        />
        <span class="field-tip">保存后则不可修改</span>
      </el-form-item>

      <el-form-item label="模板底图分辨率" required>
        <div class="resolution-row">
          <el-form-item prop="resolutionWidth" class="resolution-item">
            <el-input-number
              v-model="formData.resolutionWidth"
              :min="1"
              :controls="false"
              :disabled="isEditMode"
            />
          </el-form-item>
          <span class="resolution-separator">-</span>
          <el-form-item prop="resolutionHeight" class="resolution-item">
            <el-input-number
              v-model="formData.resolutionHeight"
              :min="1"
              :controls="false"
              :disabled="isEditMode"
            />
          </el-form-item>
          <span class="resolution-unit">px</span>
        </div>
        <div class="field-tip" style="margin-top: 15px;">后续上传模板底图请参照此分辨率上传，否则会出现文案位置偏移</div>
      </el-form-item>

      <el-form-item label="模板底图预览图" required>
        <el-upload
          action=""
          :auto-upload="false"
          :show-file-list="true"
          :file-list="previewFileList"
          :limit="1"
          accept=".jpg,.jpeg,.png"
          :on-change="handlePreviewChange"
          :on-remove="handlePreviewRemove"
        >
          <el-button size="mini" type="primary">上传预览图</el-button>
        </el-upload>
        <div class="field-tip">请上传与“模板底图分辨率”一致的图片，用于查看文案位置效果，支持上传 PNG 和 JPG 图片</div>
        <el-image
          v-if="formData.previewUrl"
          :src="formData.previewUrl"
          :preview-src-list="[formData.previewUrl]"
          fit="cover"
          class="template-preview-image"
        />
      </el-form-item>

      <div class="group-header">
        <div>
          <span>模板活动文案组</span>
          <span style="font-size: 12px; color: #999; font-weight: normal; margin-left: 5px;">
            存在多个模板活动文案组时，文案组内容始终从上往下，从左往右的顺序依次提示
          </span>
        </div>
        <el-button size="mini" type="primary" plain @click="addGroup">新增文案组</el-button>
      </div>

      <div
        v-for="(group, groupIndex) in formData.copyGroups"
        :key="group.uid"
        class="copy-group-block"
      >
        <div class="copy-group-title">
          <span>模板活动文案组{{ groupIndex + 1 }}</span>
          <el-button
            v-if="formData.copyGroups.length > 1"
            type="text"
            class="danger-text"
            @click="removeGroup(groupIndex)"
          >
            删除文案组
          </el-button>
        </div>

        <div v-for="(field, fieldIndex) in group.fields" :key="field.key" class="field-card">
          <div class="field-card-title">{{ field.label }}</div>
          <el-row :gutter="12" style="padding-right: 30px;">
            <el-col :span="8">
              <el-form-item
                label-width="90px"
                :prop="`copyGroups.${groupIndex}.fields.${fieldIndex}.x`"
                :rules="fieldRules.x"
                label="X轴位置"
              >
                <el-input-number
                  v-model="field.x"
                  :controls="false"
                  :precision="0"
                  :step="1"
                  :min="0"
                  style="width: 100%"
                />
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item
                label-width="90px"
                :prop="`copyGroups.${groupIndex}.fields.${fieldIndex}.y`"
                :rules="fieldRules.y"
                label="Y轴位置"
              >
                <el-input-number
                  v-model="field.y"
                  :controls="false"
                  :precision="0"
                  :step="1"
                  :min="0"
                  style="width: 100%"
                />
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item
                label-width="90px"
                :prop="`copyGroups.${groupIndex}.fields.${fieldIndex}.fontFamily`"
                :rules="[{ required: true, message: '请选择字体', trigger: 'change' }]"
                label="字体"
              >
                <el-select v-model="field.fontFamily" placeholder="请选择字体" style="width: 100%">
                  <el-option
                    v-for="item in fontFamilyOptions"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"
                  />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item
                label-width="90px"
                :prop="`copyGroups.${groupIndex}.fields.${fieldIndex}.fontSize`"
                :rules="[{ required: true, message: '请选择字体大小', trigger: 'change' }]"
                label="字体大小"
              >
                <el-select v-model="field.fontSize" placeholder="请选择字体大小" style="width: 100%">
                  <el-option
                    v-for="item in fontSizeOptions"
                    :key="item"
                    :label="`${item}px`"
                    :value="item"
                  />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item
                label-width="90px"
                :prop="`copyGroups.${groupIndex}.fields.${fieldIndex}.fontColor`"
                :rules="[{ required: true, message: '请选择颜色', trigger: 'change' }]"
                label="字体颜色"
              >
                <el-color-picker v-model="field.fontColor" show-alpha />
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item
                label-width="90px"
                :prop="`copyGroups.${groupIndex}.fields.${fieldIndex}.charLimit`"
                :rules="fieldRules.charLimit"
                label="字符限制"
              >
                <el-input-number
                  v-model="field.charLimit"
                  :controls="false"
                  :precision="0"
                  :step="1"
                  :min="1"
                  style="width: 100%"
                />
                <span class="field-tip">1中文=3字符，数字和英文=1字符</span>
              </el-form-item>
            </el-col>
          </el-row>
        </div>

      </div>

      <div class="sticker-section">
        <div class="sticker-header">
          <div class="sticker-title">模板贴图</div>
          <el-button size="mini" type="primary" plain @click="addSticker">新增贴图</el-button>
        </div>

        <div
          v-for="(sticker, stickerIndex) in formData.stickers"
          :key="sticker.uid"
          class="sticker-item"
        >
          <div class="sticker-item-header">
            <span>模板贴图{{ stickerIndex + 1 }}</span>
            <el-button type="text" class="danger-text" @click="removeSticker(stickerIndex)">
              删除贴图
            </el-button>
          </div>

          <el-row :gutter="12">
            <el-col :span="8">
              <el-form-item label-width="90px" label="贴图分辨率" required>
                <div class="resolution-row">
                  <el-form-item
                    :prop="`stickers.${stickerIndex}.resolution.0`"
                    :rules="stickerRules.resolutionWidth"
                    class="resolution-item"
                  >
                    <el-input-number
                      :value="sticker.resolution[0]"
                      :min="1"
                      :controls="false"
                      :precision="0"
                      :step="1"
                      @input="updateStickerValue(sticker, 'resolution', 0, $event)"
                    />
                  </el-form-item>
                  <span class="resolution-separator">-</span>
                  <el-form-item
                    :prop="`stickers.${stickerIndex}.resolution.1`"
                    :rules="stickerRules.resolutionHeight"
                    class="resolution-item"
                  >
                    <el-input-number
                      :value="sticker.resolution[1]"
                      :min="1"
                      :controls="false"
                      :precision="0"
                      :step="1"
                      @input="updateStickerValue(sticker, 'resolution', 1, $event)"
                    />
                  </el-form-item>
                  <span class="resolution-unit">px</span>
                </div>
              </el-form-item>
            </el-col>

            <el-col :span="8">
              <el-form-item
                label-width="90px"
                :prop="`stickers.${stickerIndex}.file`"
                :rules="stickerRules.file"
                label="贴图样式"
              >
                <el-upload
                  action=""
                  :auto-upload="false"
                  :show-file-list="true"
                  :file-list="getStickerFileList(sticker)"
                  :limit="1"
                  accept=".jpg,.jpeg,.png"
                  :on-change="(file, fileList) => handleStickerChange(sticker, file, fileList)"
                  :on-remove="(file, fileList) => handleStickerRemove(sticker, file, fileList)"
                >
                  <el-button size="mini" type="primary">上传贴图</el-button>
                </el-upload>
                <el-image
                  v-if="getStickerPreviewUrl(sticker)"
                  :src="getStickerPreviewUrl(sticker)"
                  :preview-src-list="[getStickerPreviewUrl(sticker)]"
                  fit="cover"
                  class="sticker-preview-image"
                />
              </el-form-item>
            </el-col>

            <el-col :span="8">
              <el-form-item label-width="90px" label="贴图位置" required>
                <div class="resolution-row">
                  <el-form-item
                    :prop="`stickers.${stickerIndex}.position.0`"
                    :rules="stickerRules.positionX"
                    class="resolution-item"
                  >
                    <el-input-number
                      :value="sticker.position[0]"
                      :min="0"
                      :controls="false"
                      :precision="0"
                      :step="1"
                      @input="updateStickerValue(sticker, 'position', 0, $event)"
                    />
                  </el-form-item>
                  <span class="resolution-separator">-</span>
                  <el-form-item
                    :prop="`stickers.${stickerIndex}.position.1`"
                    :rules="stickerRules.positionY"
                    class="resolution-item"
                  >
                    <el-input-number
                      :value="sticker.position[1]"
                      :min="0"
                      :controls="false"
                      :precision="0"
                      :step="1"
                      @input="updateStickerValue(sticker, 'position', 1, $event)"
                    />
                  </el-form-item>
                  <span class="resolution-unit">px</span>
                </div>
              </el-form-item>
            </el-col>
          </el-row>
        </div>
      </div>
    </el-form>

    <div slot="footer">
      <el-button size="mini" @click="dialogVisible = false">取消</el-button>
      <el-button type="primary" size="mini" @click="handleConfirm">确定</el-button>
    </div>
  </el-dialog>
</template>

<script>
import mockService from '../../mockService'

const COPY_FIELD_CONFIG = [
  { key: 'price', label: '到手价' },
  { key: 'rankOrMinute', label: '前*名/前*分钟' },
  { key: 'discountOrGift', label: '立减/送' }
]

function parseStickerCoordinates (value) {
  if (Array.isArray(value)) {
    return [value[0], value[1]]
  }
  if (!value) {
    return [undefined, undefined]
  }
  try {
    const coordinates = JSON.parse(value)
    return Array.isArray(coordinates)
      ? [coordinates[0], coordinates[1]]
      : [undefined, undefined]
  } catch (error) {
    return [undefined, undefined]
  }
}

function createSticker (sticker = {}) {
  return {
    uid: sticker.uid || `${Date.now()}_${Math.random()}`,
    file: sticker.file || null,
    position: parseStickerCoordinates(sticker.position),
    resolution: parseStickerCoordinates(sticker.resolution)
  }
}

function getFileUrl (file) {
  return file && file.url ? file.url : ''
}

function getFileName (file) {
  return file && file.name ? file.name : '贴图'
}

function createField (config) {
  return {
    key: config.key,
    label: config.label,
    x: undefined,
    y: undefined,
    fontFamily: '',
    fontSize: 10,
    fontColor: 'rgba(51, 51, 51, 1)',
    charLimit: undefined
  }
}

function createGroup () {
  return {
    uid: `${Date.now()}_${Math.random()}`,
    fields: COPY_FIELD_CONFIG.map(item => createField(item))
  }
}

function createDefaultForm () {
  return {
    id: '',
    storeId: '',
    templateName: '',
    resolutionWidth: undefined,
    resolutionHeight: undefined,
    previewUrl: '',
    previewFile: null,
    stickers: [createSticker()],
    copyGroups: [createGroup()]
  }
}

function getImageSize (file) {
  return new Promise((resolve, reject) => {
    const targetFile = file.raw || file
    if (!targetFile) {
      resolve({ width: undefined, height: undefined })
      return
    }
    const objectUrl = window.URL.createObjectURL(targetFile)
    const image = new Image()
    image.onload = () => {
      const size = {
        width: image.width,
        height: image.height
      }
      window.URL.revokeObjectURL(objectUrl)
      resolve(size)
    }
    image.onerror = () => {
      window.URL.revokeObjectURL(objectUrl)
      reject(new Error('图片尺寸识别失败'))
    }
    image.src = objectUrl
  })
}

export default {
  name: 'StoreDecorationTemplateDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    editData: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      storeOptions: [],
      previewFileList: [],
      stickerFileLists: {},
      stickerPreviewUrls: {},
      formData: createDefaultForm(),
      fieldRules: {
        x: [{ required: true, message: '请输入X轴位置', trigger: 'blur' }],
        y: [{ required: true, message: '请输入Y轴位置', trigger: 'blur' }],
        charLimit: [{ required: true, message: '请输入字符限制', trigger: 'blur' }]
      },
      stickerRules: {
        resolutionWidth: [{ required: true, message: '请输入贴图宽度', trigger: 'blur' }],
        resolutionHeight: [{ required: true, message: '请输入贴图高度', trigger: 'blur' }],
        file: [{ required: true, message: '请上传贴图', trigger: 'change' }],
        positionX: [{ required: true, message: '请输入贴图X轴位置', trigger: 'blur' }],
        positionY: [{ required: true, message: '请输入贴图Y轴位置', trigger: 'blur' }]
      },
      fontFamilyOptions: [],
      rules: {
        storeId: [
          { required: true, message: '请选择店铺', trigger: 'change' }
        ],
        templateName: [
          { required: true, message: '请输入模板名称', trigger: 'blur' }
        ],
        resolutionWidth: [
          { required: true, message: '请输入宽度', trigger: 'blur' }
        ],
        resolutionHeight: [
          { required: true, message: '请输入高度', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    dialogVisible: {
      get () {
        return this.visible
      },
      set (val) {
        this.$emit('update:visible', val)
      }
    },
    dialogTitle () {
      return this.formData.id ? '编辑模板' : '新增模板'
    },
    isEditMode () {
      return !!this.formData.id
    },
    fontSizeOptions () {
      const arr = []
      for (let i = 10; i <= 140; i += 2) {
        arr.push(i)
      }
      return arr
    }
  },
  watch: {
    visible (val) {
      if (val) {
        this.initOptions()
        this.initFormData()
      }
    }
  },
  methods: {
    initOptions () {
      mockService.getStoreOptions().then(res => {
        this.storeOptions = res.data || []
      })
      mockService.getFontOptions().then(res => {
        this.fontFamilyOptions = res.data || []
      })
    },
    initFormData () {
      this.clearStickerUploadState()
      if (!this.editData) {
        this.formData = createDefaultForm()
        this.previewFileList = []
        return
      }
      const row = JSON.parse(JSON.stringify(this.editData))
      if (row.baseImageUrl) {
        row.previewImageUrl = row.baseImageUrl
        row.previewUrl = row.baseImageUrl
      }
      const previewUrl = row.previewUrl || row.previewImageUrl || ''
      this.formData = {
        id: row.id,
        storeId: row.storeId,
        templateName: row.templateName,
        resolutionWidth: row.resolutionWidth,
        resolutionHeight: row.resolutionHeight,
        previewUrl,
        previewFile: null,
        stickers: Array.isArray(row.stickers)
          ? row.stickers.map(sticker => createSticker(sticker))
          : [],
        copyGroups: (row.copyGroups || []).map(group => {
          return {
            uid: group.uid || `${Date.now()}_${Math.random()}`,
            fields: COPY_FIELD_CONFIG.map(config => {
              const current = (group.fields || []).find(item => item.key === config.key) || {}
              return {
                key: config.key,
                label: config.label,
                x: current.x,
                y: current.y,
                fontFamily: current.fontFamily || 'SimSun',
                fontSize: current.fontSize || 10,
                fontColor: current.fontColor || 'rgba(51, 51, 51, 1)',
                charLimit: current.charLimit
              }
            })
          }
        })
      }
      this.initStickerUploadState()
      this.previewFileList = previewUrl
        ? [{ name: row.templateName || '预览图', url: previewUrl }]
        : []
    },
    addGroup () {
      const group = createGroup()
      this.formData.copyGroups.push(group)
    },
    removeGroup (index) {
      this.$confirm('确认删除此文案组吗？', '提示', { type: 'warning' }).then(() => {
        this.formData.copyGroups.splice(index, 1)
      }).catch(() => {})
    },
    addSticker () {
      const sticker = createSticker()
      this.formData.stickers.push(sticker)
      this.$set(this.stickerFileLists, sticker.uid, [])
    },
    removeSticker (index) {
      this.$confirm('确认删除此贴图吗？', '提示', { type: 'warning' }).then(() => {
        const sticker = this.formData.stickers[index]
        this.clearStickerUploadState(sticker)
        this.formData.stickers.splice(index, 1)
      }).catch(() => {})
    },
    updateStickerValue (sticker, key, index, value) {
      if (!Array.isArray(sticker[key])) {
        this.$set(sticker, key, [undefined, undefined])
      }
      this.$set(sticker[key], index, value)
    },
    getStickerFileList (sticker) {
      return this.stickerFileLists[sticker.uid] || []
    },
    getStickerPreviewUrl (sticker) {
      return this.stickerPreviewUrls[sticker.uid] || getFileUrl(sticker.file)
    },
    initStickerUploadState () {
      this.formData.stickers.forEach(sticker => {
        const file = sticker.file
        const url = getFileUrl(file)
        this.$set(this.stickerFileLists, sticker.uid, url
          ? [{ name: getFileName(file), url }]
          : [])
        if (url) {
          this.$set(this.stickerPreviewUrls, sticker.uid, url)
        }
      })
    },
    clearStickerUploadState (sticker) {
      if (sticker) {
        const previewUrl = this.stickerPreviewUrls[sticker.uid]
        if (previewUrl && previewUrl.indexOf('blob:') === 0 && typeof window !== 'undefined' && window.URL) {
          window.URL.revokeObjectURL(previewUrl)
        }
        this.$delete(this.stickerFileLists, sticker.uid)
        this.$delete(this.stickerPreviewUrls, sticker.uid)
        return
      }
      Object.keys(this.stickerPreviewUrls).forEach(uid => {
        const previewUrl = this.stickerPreviewUrls[uid]
        if (previewUrl && previewUrl.indexOf('blob:') === 0 && typeof window !== 'undefined' && window.URL) {
          window.URL.revokeObjectURL(previewUrl)
        }
      })
      this.stickerFileLists = {}
      this.stickerPreviewUrls = {}
    },
    handleStickerChange (sticker, file, fileList) {
      const name = (file.raw && file.raw.name) || file.name || ''
      const ext = name.substring(name.lastIndexOf('.') + 1).toLowerCase()
      if (['jpg', 'jpeg', 'png'].indexOf(ext) === -1) {
        this.$message.error('仅支持上传 PNG 和 JPG 图片')
        this.handleStickerRemove(sticker)
        return
      }

      const targetFile = file.raw || file
      this.updateStickerValue(sticker, 'resolution', 0, undefined)
      this.updateStickerValue(sticker, 'resolution', 1, undefined)

      const previousPreviewUrl = this.stickerPreviewUrls[sticker.uid]
      if (previousPreviewUrl && previousPreviewUrl.indexOf('blob:') === 0 && typeof window !== 'undefined' && window.URL) {
        window.URL.revokeObjectURL(previousPreviewUrl)
      }
      const previewUrl = typeof window !== 'undefined' && window.URL && window.URL.createObjectURL
        ? window.URL.createObjectURL(targetFile)
        : ''
      this.$set(sticker, 'file', { name, url: previewUrl, raw: targetFile })
      this.$set(this.stickerFileLists, sticker.uid, fileList.slice(-1))
      this.$set(this.stickerPreviewUrls, sticker.uid, previewUrl)

      getImageSize(file).then(({ width, height }) => {
        if (!sticker.file || sticker.file.url !== previewUrl) return
        this.updateStickerValue(sticker, 'resolution', 0, width)
        this.updateStickerValue(sticker, 'resolution', 1, height)
        this.$nextTick(() => {
          if (this.$refs.formRef) {
            const stickerIndex = this.formData.stickers.indexOf(sticker)
            this.$refs.formRef.clearValidate([
              `stickers.${stickerIndex}.resolution.0`,
              `stickers.${stickerIndex}.resolution.1`
            ])
          }
        })
      }).catch((error) => {
        this.$message.error(error.message || '贴图尺寸识别失败')
      })
    },
    uploadStickerFiles () {
      return Promise.all(this.formData.stickers.map(sticker => {
        const file = sticker.file || {}
        const uploadPromise = file.raw
          ? mockService.uploadAppFile(file.raw)
          : Promise.resolve(file.url || '')
        return uploadPromise.then(fileUrl => {
          if (file.raw && !fileUrl) {
            throw new Error('贴图上传失败')
          }
          const stickerFile = {
            name: file.name || '',
            url: fileUrl
          }
          this.$set(sticker, 'file', stickerFile)
          return {
            file: stickerFile,
            position: sticker.position,
            resolution: sticker.resolution
          }
        })
      }))
    },
    handleStickerRemove (sticker) {
      this.updateStickerValue(sticker, 'resolution', 0, undefined)
      this.updateStickerValue(sticker, 'resolution', 1, undefined)
      this.$set(sticker, 'file', null)
      this.$set(this.stickerFileLists, sticker.uid, [])
      const previewUrl = this.stickerPreviewUrls[sticker.uid]
      if (previewUrl && previewUrl.indexOf('blob:') === 0 && typeof window !== 'undefined' && window.URL) {
        window.URL.revokeObjectURL(previewUrl)
      }
      this.$delete(this.stickerPreviewUrls, sticker.uid)
    },
    handlePreviewChange (file, fileList) {
      const name = (file.raw && file.raw.name) || file.name || ''
      const ext = name.substring(name.lastIndexOf('.') + 1).toLowerCase()
      if (['jpg', 'jpeg', 'png'].indexOf(ext) === -1) {
        this.$message.error('仅支持上传 PNG 和 JPG 图片')
        this.previewFileList = []
        this.formData.previewFile = null
        this.formData.previewUrl = ''
        return
      }
      this.previewFileList = fileList.slice(-1)
      this.formData.previewFile = file.raw || file
      this.formData.previewUrl = window.URL.createObjectURL(file.raw || file)
      getImageSize(file).then(({ width, height }) => {
        this.formData.resolutionWidth = width
        this.formData.resolutionHeight = height
        this.$nextTick(() => {
          if (this.$refs.formRef) {
            this.$refs.formRef.clearValidate(['resolutionWidth', 'resolutionHeight'])
          }
        })
      }).catch((error) => {
        this.$message.error(error.message || '图片尺寸识别失败')
      })
    },
    handlePreviewRemove (file, fileList) {
      this.previewFileList = fileList
      this.formData.previewFile = null
      if (!fileList.length) {
        this.formData.previewUrl = ''
      }
    },
    validatePreview () {
      if (!this.formData.previewUrl && !this.editData) {
        this.$message.error('请上传模板底图预览图')
        return false
      }
      return true
    },
    handleConfirm () {
      this.$refs.formRef.validate(valid => {
        if (!valid) return
        if (!this.validatePreview()) return
        this.uploadStickerFiles().then(stickers => {
          const store = this.storeOptions.find(item => item.value === this.formData.storeId) || {}
          this.$emit('confirm', {
            id: this.formData.id,
            storeId: this.formData.storeId,
            storeName: store.label || '',
            templateName: this.formData.templateName,
            resolutionWidth: this.formData.resolutionWidth,
            resolutionHeight: this.formData.resolutionHeight,
            previewUrl: this.formData.previewUrl,
            previewFile: this.formData.previewFile,
            stickers,
            copyGroups: this.formData.copyGroups
          })
        }).catch(error => {
          this.$message.error((error && error.message) || '贴图上传失败')
        })
      })
    },
    resetForm () {
      this.clearStickerUploadState()
      this.formData = createDefaultForm()
      this.previewFileList = []
      if (this.$refs.formRef) {
        this.$refs.formRef.clearValidate()
      }
    }
  }
}
</script>

<style lang="stylus" scoped>
.dialog-form
  max-height 70vh
  overflow-y auto
  padding-right 10px

.resolution-row
  display flex
  align-items center

.resolution-item
  margin-bottom 0
  width 90px
  >>> .el-input
    width 90px

.resolution-separator
  padding 0 10px
  color #606266

.resolution-unit
  padding-left 10px
  color #606266

.field-tip
  line-height 20px
  font-size 12px
  color #909399
  white-space nowrap

.template-preview-image
  margin-top 10px
  width 180px
  height 120px
  object-fit cover
  border 1px solid #ebeef5
  border-radius 4px

.group-header
  display flex
  align-items center
  justify-content space-between
  margin 16px 0 12px
  padding-top 8px
  border-top 1px solid #ebeef5
  font-weight 600

.copy-group-block
  margin-bottom 16px
  padding 16px
  background #fafafa
  border 1px solid #ebeef5
  border-radius 4px

.copy-group-title
  display flex
  align-items center
  justify-content space-between
  margin-bottom 12px
  font-weight 600

.field-card
  margin-bottom 12px
  padding 12px
  background #fff
  border-radius 4px
  border 1px dashed #dcdfe6

.field-card-title
  margin-bottom 10px
  color #303133
  font-weight 600

.sticker-section
  margin-top 16px
  padding-top 16px
  border-top 1px solid #ebeef5

.sticker-header
  display flex
  align-items center
  justify-content space-between
  margin-bottom 12px

.sticker-title
  color #303133
  font-weight 600

.sticker-item
  margin-bottom 12px
  padding 12px
  background #fafafa
  border 1px solid #ebeef5
  border-radius 4px

.sticker-item-header
  display flex
  align-items center
  justify-content space-between
  margin-bottom 12px
  font-weight 600

.sticker-preview-image
  display block
  margin-top 10px
  width 90px
  height 90px
  object-fit contain
  border 1px solid #ebeef5
  border-radius 4px

.danger-text
  color #F56C6C

>>> .resolution-item .el-form-item__content
  margin-left 0 !important
</style>
